import React, { FC, useMemo, useRef, useState } from 'react';
import styles from '../journey-explorer.module.scss';
import { useJourneyApi } from '../journey-api-context';
import {
	JourneyCustomersPage,
	JourneyFilterState,
	JourneySegment,
} from '../../../../models/journey-explorer/journey-explorer';
import { formatDate, formatNumber } from '../journey-explorer-utils';
import { RequestLoader, useDebouncedRequest } from '../use-debounced-request';
import ExportCustomers from './export-customers';

const PAGE_SIZE = 25;
const LOAD_ERROR = 'The customers could not be loaded.';

interface SegmentCustomersProps {
	filters: JourneyFilterState;
	segment: JourneySegment;
	onOpenCustomer: (customerId: string) => void;
}

const SegmentCustomers: FC<SegmentCustomersProps> = ({ filters, segment, onOpenCustomer }) => {
	const apiService = useJourneyApi();
	const apiRef = useRef(apiService);
	apiRef.current = apiService;
	const [page, setPage] = useState(1);
	const [attempt, setAttempt] = useState(0);

	const load = useMemo<RequestLoader<JourneyCustomersPage>>(
		() => (signal) => apiRef.current.getCustomers({ filters, segment, page, page_size: PAGE_SIZE }, signal),
		[filters, segment, page]
	);
	const { data, loading, error } = useDebouncedRequest(load, LOAD_ERROR, attempt);

	if (error) {
		return (
			<div className={styles.drawer_message}>
				{error}
				<button type="button" className={styles.retry} onClick={() => setAttempt((value) => value + 1)}>
					Retry
				</button>
			</div>
		);
	}
	if (!data) {
		return (
			<div className={styles.drawer_message}>
				<span className={styles.spinner} aria-label="Loading" />
			</div>
		);
	}

	const pageCount = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

	return (
		<div className={loading ? styles.loading : undefined}>
			<p className={styles.drawer_intro}>
				{formatNumber(data.total)} customers. Showing {formatNumber(data.customers.length)}. Click one to see
				their full journey.
			</p>
			<div className={styles.drawer_export}>
				<ExportCustomers filters={filters} segment={segment} customers={data.total} compact />
			</div>
			{data.customers.map((customer) => {
				const channel = Object.values(customer.attributes).find((value) => value);
				const details = [customer.start_date && `Started ${formatDate(customer.start_date)}`, channel].filter(
					Boolean
				);
				return (
					<button
						key={customer.customer_id}
						type="button"
						className={styles.customer_card}
						onClick={() => onOpenCustomer(customer.customer_id)}
					>
						<span className={styles.customer_card_top}>
							<span className={styles.customer_card_id}>{customer.customer_id}</span>
							<span className={customer.goal_date ? styles.goal_text : styles.active_text}>
								{customer.goal_date ? `Reached ${formatDate(customer.goal_date)}` : 'Not reached'}
							</span>
						</span>
						<span className={styles.customer_card_details}>{details.join(' · ')}</span>
						<span className={styles.customer_card_link}>
							{customer.journey.length} interactions <span aria-hidden="true">›</span>
						</span>
					</button>
				);
			})}
			{pageCount > 1 && (
				<div className={styles.drawer_pager}>
					<button
						type="button"
						className={styles.link}
						disabled={page <= 1 || loading}
						onClick={() => setPage((value) => value - 1)}
					>
						Previous
					</button>
					<span>
						Page {page} of {formatNumber(pageCount)}
					</span>
					<button
						type="button"
						className={styles.link}
						disabled={page >= pageCount || loading}
						onClick={() => setPage((value) => value + 1)}
					>
						Next
					</button>
				</div>
			)}
		</div>
	);
};

export default SegmentCustomers;
